import { ImpressaoAtendimentoIndividualInput } from 'api/ImpressaoAtendimentoIndividual'
import { Alert, Button, HFlow, Theme, VFlow } from 'bold-ui'
import { useAlert } from 'components/alert'
import useSession from 'components/auth/useSession'
import { DateTime } from 'components/date'
import { useErrorHandler } from 'components/error'
import { CheckboxField, Form, FormRenderProps, FormValueSpy } from 'components/form'
import { AccordionField } from 'components/form/field/AccordionField'
import updateValue from 'components/form/mutators/updateValue'
import { PageContent } from 'components/layout/PageContent'
import { confirm } from 'components/modals/confirm'
import { Decorator, Subscription } from 'final-form'
import arrayMutators from 'final-form-arrays'
import { useApolloClient } from 'graphql/hooks'
import { useCancelarAtendimentoMutation, useFinalizarAtendimentoIndividualMutation } from 'graphql/hooks.generated'
import { AtendimentoIndividualViewQuery, TipoAtendimentoProfissional } from 'graphql/types.generated'
import { useCabecalhoMinimizado } from 'hooks/useCabecalhoMinimizado'
import React, { CSSProperties, Dispatch, memo, useCallback, useEffect, useMemo, useState } from 'react'
import { AccordionItem } from 'react-accessible-accordion'
import { FormSpy } from 'react-final-form'
import { useHistory, useRouteMatch } from 'react-router'
import { Route, Switch } from 'react-router-dom'
import { createAtendimentoIndividualCalculator } from 'view/atendimentos/atendimento-individual/calculator'
import { converAtendimentoIndividualModelToInput } from 'view/atendimentos/atendimento-individual/converter'
import { meta, SoapState, TipoServicoSoapView } from 'view/atendimentos/atendimento-individual/model'
import { atendimentoIndividualValidator } from 'view/atendimentos/atendimento-individual/validator'
import { AgendamentoDia } from 'view/atendimentos/types/AgendamentoDia'
import { CidadaoAtendimento } from 'view/atendimentos/types/CidadaoAtendimento'

import { ProcedimentosAutomaticos, useLocalStyles } from '../../atendimento-individual/AtendimentoIndividualView'
import { downloadAtendimentoIndividual } from '../components/downloadAtendimentoIndividual'
import { ListaProblemasModal } from '../components/modals/lista-problemas/ListaProblemasModal'
import MedicamentosAtivosConcluidosModal from '../components/modals/medicamentos/MedicamentosAtivosConcluidosModal'
import { HistoricoMedicoesModal } from '../components/modals/medicoes/HistoricoMedicoesModal'
import { AsideView } from './aside/AsideView'
import { AvaliacaoSection } from './avaliacao/AvaliacaoSection'
import { DesfechoSection } from './desfecho'
import { ObjetivoSection } from './objetivo'
import { PlanoSection } from './plano/PlanoSection'
import { SubjetivoSection } from './subjetivo'

const subscription: Subscription = {} /* evita problemas de performance */

const createDecorators = (
  apollo,
  procedimentosAutomaticos: ProcedimentosAutomaticos,
  isMedico: boolean,
  tipoAtendProf: TipoAtendimentoProfissional
): Decorator[] => [createAtendimentoIndividualCalculator(apollo, procedimentosAutomaticos, isMedico, tipoAtendProf)]

interface SoapViewProps {
  atendimentoId: ID
  prontuarioId: ID
  cidadao: CidadaoAtendimento
  tiposServico: TipoServicoSoapView[]
  dataAtendimento: Instant
  procedimentosAutomaticos?: ProcedimentosAutomaticos
  isMedico: boolean
  isDemandaEspontanea: boolean
  isAtendimentoOdonto?: boolean
  agendamento?: AtendimentoIndividualViewQuery['atendimento']['agendado']
  agendamentosDia: AgendamentoDia[]
  tipoAtendProf: TipoAtendimentoProfissional
  cacheState: SoapState
  updateCache: Dispatch<SoapState>
  clearCache: Dispatch<void>
}

export function SoapView(props: SoapViewProps) {
  const { cacheState } = props
  const [initialValues] = useState(cacheState) // usado pra setar os valores iniciais do Form apenas na primeira vez que renderizar

  return (
    <div>
      <SoapForm {...props} initialValues={initialValues} />
    </div>
  )
}

interface SoapFormProps {
  atendimentoId: ID
  prontuarioId: ID
  initialValues: SoapState
  cidadao: CidadaoAtendimento
  dataAtendimento: Instant
  procedimentosAutomaticos?: ProcedimentosAutomaticos
  isDemandaEspontanea: boolean
  isMedico: boolean
  isAtendimentoOdonto?: boolean
  agendamentosDia: AgendamentoDia[]
  tipoAtendProf: TipoAtendimentoProfissional
  updateCache: Dispatch<SoapState>
  clearCache: Dispatch<void>
}

const SoapForm = memo((props: SoapFormProps) => {
  const {
    atendimentoId,
    prontuarioId,
    cidadao,
    agendamentosDia,
    dataAtendimento,
    procedimentosAutomaticos,
    initialValues,
    isDemandaEspontanea,
    isMedico,
    isAtendimentoOdonto,
    tipoAtendProf,
    updateCache,
    clearCache,
  } = props
  const { classes } = useLocalStyles(createStyles) // o do bold retorna classes novas a todo render
  const history = useHistory()
  const [finalizar] = useFinalizarAtendimentoIndividualMutation()
  const [cancelar] = useCancelarAtendimentoMutation()
  const handleRejection = useErrorHandler()
  const { data } = useSession()
  const match = useRouteMatch()
  const apollo = useApolloClient()
  const decorators = useMemo(() => createDecorators(apollo, procedimentosAutomaticos, isMedico, tipoAtendProf), [
    apollo,
    isMedico,
    procedimentosAutomaticos,
    tipoAtendProf,
  ])
  const validators = useMemo(
    () => atendimentoIndividualValidator(cidadao, dataAtendimento, tipoAtendProf, isDemandaEspontanea),
    [cidadao, dataAtendimento, isDemandaEspontanea, tipoAtendProf]
  )
  const alert = useAlert()
  const isCabecalhoMinimizado = useCabecalhoMinimizado()
  const tipoAtendString = isAtendimentoOdonto ? 'odontológico' : 'individual'

  const printItem = (itemToPrint: ImpressaoAtendimentoIndividualInput) => {
    downloadAtendimentoIndividual(itemToPrint)
  }

  const [formInitialized, setFormInitialized] = useState(false)
  useEffect(() => {
    setFormInitialized(true)
  }, [])

  const handleFormChange = useCallback(
    (values: SoapState) => {
      /* Avoids the error of updating a component while rendering a different component. */
      if (formInitialized) {
        values.lastSaved = new Date()
        updateCache(values)
      }
    },
    [formInitialized, updateCache]
  )

  const handleCancelar = useCallback(
    () =>
      confirm({
        title: `Deseja cancelar o atendimento ${tipoAtendString}?`,
        body: 'As alterações realizadas serão perdidas.',
        confirmLabel: 'Sim',
        cancelLabel: 'Não',
        onConfirm: () => {
          cancelar({ variables: { atendimentoId } })
            .then(() => {
              clearCache()
              history.push('/lista-atendimento')
            })
            .catch(handleRejection)
        },
      })(),
    [atendimentoId, cancelar, clearCache, handleRejection, history, tipoAtendString]
  )

  const handleFinalizar = useCallback(
    (values: SoapState) =>
      finalizar({
        variables: {
          input: converAtendimentoIndividualModelToInput(atendimentoId, cidadao.id, values),
        },
      }).then(() => {
        values.imprimirAtendimento && printItem({ atendimentoId })
        alert('success', `Atendimento ${tipoAtendString} finalizado com sucesso.`)
        clearCache()
        history.push('/lista-atendimento')
      }),
    [alert, atendimentoId, cidadao.id, clearCache, finalizar, history, tipoAtendString]
  )

  const handleOnFocus = useCallback(() => {
    const activeElement = document.activeElement as HTMLElement
    if (activeElement) {
      const activeElementTop = activeElement.getBoundingClientRect().top
      activeElementTop < 0 && window.scrollTo(0, activeElement.parentElement.offsetTop - 160)
    }
  }, [])

  const renderForm = useCallback(
    (formProps: FormRenderProps<SoapState>) => {
      const { handleSubmit } = formProps
      return (
        <>
          <form noValidate onFocus={handleOnFocus}>
            <PageContent fluid type='filled'>
              <div className={classes.container}>
                {isCabecalhoMinimizado && <div style={{ height: '100%' }} /> /*Placeholder do aside na grid*/}
                <FormSpy<SoapState> subscription={{ values: true }}>
                  {({ values: formValues }) => (
                    <AsideView
                      prontuarioId={prontuarioId}
                      dataAtendimento={dataAtendimento}
                      cidadaoId={cidadao.id}
                      problemasAvaliados={formValues?.avaliacao?.condicoes}
                    />
                  )}
                </FormSpy>
                <AccordionField allowMultipleExpanded={true} allowZeroExpanded={true} name={meta.openedAccordions}>
                  <FormValueSpy<SoapState> onChange={handleFormChange} />
                  <VFlow>
                    <AccordionItem uuid='S'>
                      <SubjetivoSection name={meta.subjetivo} cidadao={cidadao} tipoAtendProf={tipoAtendProf} />
                    </AccordionItem>
                    <AccordionItem uuid='O'>
                      <ObjetivoSection
                        name={meta.objetivo}
                        cidadao={cidadao}
                        dataAtendimento={dataAtendimento}
                        tipoAtendProf={tipoAtendProf}
                      />
                    </AccordionItem>
                    <AccordionItem uuid='A'>
                      <AvaliacaoSection
                        name={meta.avaliacao}
                        cidadao={cidadao}
                        prontuarioId={prontuarioId}
                        acessoProfissional={data.acesso}
                        tipoAtendProf={tipoAtendProf}
                        dataAtendimento={dataAtendimento}
                      />
                    </AccordionItem>
                    <AccordionItem uuid='P'>
                      <PlanoSection
                        name={meta.plano}
                        cidadao={cidadao}
                        prontuarioId={prontuarioId}
                        dataAtendimento={dataAtendimento}
                        tipoAtendProf={tipoAtendProf}
                        isAtendimentoOdonto={isAtendimentoOdonto}
                        atendimentoId={atendimentoId}
                      />
                    </AccordionItem>
                    <AccordionItem uuid='D'>
                      <DesfechoSection
                        name={meta.desfecho}
                        cidadao={cidadao}
                        profissionalId={data.profissional.id}
                        dataAtendimento={dataAtendimento}
                        isDemandaEspontanea={isDemandaEspontanea}
                        agendamentosDia={agendamentosDia}
                        tipoAtendProf={tipoAtendProf}
                      />
                    </AccordionItem>
                  </VFlow>
                </AccordionField>
              </div>
            </PageContent>
            <div className={classes.actions}>
              <PageContent type='filled' fluid>
                <HFlow justifyContent='space-between'>
                  <FormSpy<SoapState> subscription={{ dirty: true, active: true, values: true }}>
                    {(props) => (
                      <Alert inline type='success' styles={{ wrapper: classes.draftAlertWrapper }}>
                        Rascunho salvo automaticamente às <DateTime format='HH:mm' value={props.values.lastSaved} />
                      </Alert>
                    )}
                  </FormSpy>
                  <HFlow>
                    <CheckboxField
                      name={meta.imprimirAtendimento}
                      label='Imprimir atendimento ao finalizar'
                      style={classes.print}
                    />
                    <Button kind='normal' onClick={handleCancelar}>
                      Cancelar atendimento {tipoAtendString}
                    </Button>
                    <Button kind='primary' onClick={handleSubmit}>
                      Finalizar atendimento {tipoAtendString}
                    </Button>
                  </HFlow>
                </HFlow>
              </PageContent>
            </div>
          </form>
          <Switch>
            <Route
              path={`${match.url}/lista-problemas-condicoes`}
              render={(renderProps) => (
                <FormSpy<SoapState> subscription={{ values: true }}>
                  {({ values: formValues }) => (
                    <ListaProblemasModal
                      {...renderProps}
                      prontuarioId={prontuarioId}
                      dataNascimentoCidadao={cidadao?.dataNascimento}
                      problemasAvaliados={formValues?.avaliacao?.condicoes}
                    />
                  )}
                </FormSpy>
              )}
            />
            <Route
              path={`${match.url}/historicoMedicoes`}
              render={(renderProps) => (
                <FormSpy<SoapState> subscription={{ values: true }}>
                  {(props) => (
                    <HistoricoMedicoesModal
                      {...renderProps}
                      cidadao={cidadao}
                      dataAtendimento={dataAtendimento}
                      name={meta.medicoesAnteriores}
                      medicoesAtendimento={props.values?.objetivo?.medicoes}
                      prontuarioId={prontuarioId}
                    />
                  )}
                </FormSpy>
              )}
            />
            <Route
              path={`${match.url}/medicamentos-ativos-concluidos`}
              render={() => (
                <MedicamentosAtivosConcluidosModal prontuarioId={prontuarioId} dataAtendimento={dataAtendimento} />
              )}
            />
          </Switch>
        </>
      )
    },
    [
      handleOnFocus,
      classes.container,
      classes.actions,
      classes.print,
      classes.draftAlertWrapper,
      isCabecalhoMinimizado,
      handleFormChange,
      cidadao,
      tipoAtendProf,
      dataAtendimento,
      prontuarioId,
      data.acesso,
      data.profissional.id,
      isAtendimentoOdonto,
      atendimentoId,
      isDemandaEspontanea,
      agendamentosDia,
      handleCancelar,
      tipoAtendString,
      match.url,
    ]
  )

  return (
    <Form<SoapState>
      render={renderForm}
      initialValues={initialValues}
      decorators={decorators}
      validateOnBlur
      validate={validators}
      subscription={subscription}
      onSubmit={handleFinalizar}
      mutators={{
        ...arrayMutators,
        updateValue,
      }}
    />
  )
})

const createStyles = (theme: Theme) => ({
  container: {
    display: 'grid',
    gridTemplateColumns: 'calc(100% / 12 * 3.5) 1fr',
    gridGap: '1rem',
    [theme.breakpoints.down('lg')]: {
      gridTemplateColumns: 'calc(100% / 12 * 3) 1fr',
    },
  },
  content: {
    padding: '1rem 0',
  },
  print: {
    border: `0.063rem solid ${theme.pallete.gray.c80}`,
    borderRadius: '0.125rem',
    padding: '0.75rem 1rem',
  },
  actions: {
    borderTop: `1px solid ${theme.pallete.divider}`,
    height: '6.125rem',
    position: 'relative',
  } as CSSProperties,
  draftAlertWrapper: {
    backgroundColor: 'transparent',
    borderWidth: 0,
  },
})
